import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Target, 
  Users, 
  Trophy, 
  Star, 
  ChevronRight, 
  Play,
  BookOpen,
  Zap,
  Shield,
  Crosshair,
  Map,
  Headphones
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { getBreadcrumbSchema, getFAQSchema, getHowToSchema, getGameSchema } from "@/utils/structuredData";
import { supabase } from "@/integrations/supabase/client";

const ComoJogarValorant = () => {
  const navigate = useNavigate();
  const [mentorCount, setMentorCount] = useState(0);

  // Buscar número de mentores de Valorant
  useEffect(() => {
    const fetchMentorCount = async () => {
      try {
        const { data: gameData } = await supabase
          .from("games")
          .select("id")
          .ilike("name", "%valorant%")
          .single();

        if (gameData) {
          const { data: offerings } = await supabase
            .from("mentorship_offerings")
            .select("mentor_id")
            .eq("game_id", gameData.id)
            .eq("is_active", true);

          const uniqueMentors = new Set(offerings?.map(o => o.mentor_id) || []);
          setMentorCount(uniqueMentors.size);
        }
      } catch (error) {
        console.error("Erro ao buscar mentores:", error);
      }
    };

    fetchMentorCount();
  }, []);

  // FAQ para dados estruturados
  const faqs = [
    {
      question: "Como começar a jogar Valorant?",
      answer: "Para começar no Valorant, baixe o jogo gratuitamente, complete o tutorial, pratique no campo de tiro e comece com agentes mais simples como Sage ou Phoenix."
    },
    {
      question: "Qual é o melhor agente para iniciantes?",
      answer: "Sage é ideal para iniciantes por ter habilidades de suporte e cura. Phoenix também é uma boa opção por ser mais direto e ter habilidades de auto-sustentação."
    },
    {
      question: "Como melhorar a mira no Valorant?",
      answer: "Pratique diariamente no campo de tiro, ajuste sua sensibilidade, use mapas de aim training como Aim Lab ou Kovaak's, e mantenha o crosshair na altura da cabeça."
    },
    {
      question: "Quanto tempo leva para ficar bom no Valorant?",
      answer: "Com prática consistente, você pode ver melhorias significativas em 2-3 meses. Para alcançar níveis competitivos altos, pode levar de 6 meses a 2 anos."
    },
    {
      question: "Vale a pena ter um mentor de Valorant?",
      answer: "Sim! Um mentor acelera seu aprendizado, corrige erros específicos, ensina estratégias avançadas e ajuda você a subir de rank mais rapidamente."
    }
  ];

  // Passos para dados estruturados HowTo
  const howToSteps = [
    {
      name: "Baixar e Instalar",
      text: "Baixe o Valorant gratuitamente no site oficial da Riot Games e complete a instalação."
    },
    {
      name: "Tutorial Completo",
      text: "Complete todo o tutorial para aprender os controles básicos e mecânicas do jogo."
    },
    {
      name: "Escolher Agente",
      text: "Comece com agentes simples como Sage, Phoenix ou Brimstone para aprender o básico."
    },
    {
      name: "Praticar Mira",
      text: "Dedique 15-30 minutos diários no campo de tiro para melhorar sua precisão."
    },
    {
      name: "Jogar Partidas",
      text: "Comece com partidas não ranqueadas para ganhar experiência sem pressão."
    },
    {
      name: "Estudar Mapas",
      text: "Aprenda os callouts e posições estratégicas de cada mapa."
    }
  ];

  // SEO otimizado
  useSEO({
    title: "Como Jogar Valorant - Guia Completo 2024 para Iniciantes",
    description: "Aprenda como jogar Valorant do zero! Guia completo com dicas de mira, melhores agentes, estratégias e como subir de rank. Mentores profissionais disponíveis.",
    keywords: "como jogar valorant, valorant iniciantes, guia valorant, dicas valorant, mentor valorant, coach valorant, mira valorant, agentes valorant",
    canonicalUrl: "https://arena-pro-player.com/como-jogar-valorant",
    structuredData: [
      getBreadcrumbSchema([
        { name: "Início", url: "https://arena-pro-player.com/" },
        { name: "Jogos", url: "https://arena-pro-player.com/jogos" },
        { name: "Como Jogar Valorant", url: "https://arena-pro-player.com/como-jogar-valorant" }
      ]),
      getFAQSchema(faqs),
      getHowToSchema("Como Jogar Valorant", "Guia completo para iniciantes aprenderem Valorant", howToSteps),
      getGameSchema({ name: "Valorant", cover_url: "/valorant-cover.jpg" }, mentorCount)
    ]
  });

  return (
    <div className="min-h-screen bg-[#0f141c] text-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-[#0f141c] via-[#1a2332] to-[#0f141c]">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-center opacity-5"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Badge className="mb-4 bg-[#FF4655] text-white">
              <Target className="h-4 w-4 mr-2" />
              Valorant
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Como Jogar <span className="text-[#FF4655]">Valorant</span>
              <br />
              <span className="text-2xl md:text-3xl text-gray-300">Guia Completo 2024</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Aprenda Valorant do zero com nosso guia completo! Dicas de mira, melhores agentes, 
              estratégias e como subir de rank rapidamente com mentores profissionais.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#FF4655] hover:bg-[#E63946] text-white"
                onClick={() => navigate("/mentor-valorant")}
              >
                <Users className="h-5 w-5 mr-2" />
                Encontrar Mentor de Valorant
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-[#FF4655] text-[#FF4655] hover:bg-[#FF4655] hover:text-white"
                onClick={() => navigate("/mentorias?game=valorant")}
              >
                <Play className="h-5 w-5 mr-2" />
                Ver Mentorias
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Estatísticas */}
      <section className="py-16 px-4 bg-[#181D24]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-[#FF4655] mb-2">{mentorCount}+</div>
              <div className="text-gray-300">Mentores Especialistas</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-[#FF4655] mb-2">150M+</div>
              <div className="text-gray-300">Jogadores Mundiais</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-[#FF4655] mb-2">5v5</div>
              <div className="text-gray-300">FPS Tático</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* O que é Valorant */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              O que é <span className="text-[#FF4655]">Valorant</span>?
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Valorant é um jogo de tiro tático 5v5 desenvolvido pela Riot Games. 
              Combina precisão de mira com habilidades únicas de agentes, criando 
              um gameplay estratégico e competitivo.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="bg-[#181D24] border-[#222A35]">
              <CardHeader>
                <Crosshair className="h-8 w-8 text-[#FF4655] mb-2" />
                <CardTitle className="text-white">Precisão é Tudo</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">
                  Valorant premia a precisão. Cada tiro conta e a mira é fundamental 
                  para o sucesso no jogo.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#181D24] border-[#222A35]">
              <CardHeader>
                <Zap className="h-8 w-8 text-[#FF4655] mb-2" />
                <CardTitle className="text-white">Habilidades Únicas</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">
                  Cada agente possui habilidades especiais que complementam 
                  o gameplay de tiro tradicional.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#181D24] border-[#222A35]">
              <CardHeader>
                <Users className="h-8 w-8 text-[#FF4655] mb-2" />
                <CardTitle className="text-white">Trabalho em Equipe</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">
                  Comunicação e coordenação com a equipe são essenciais 
                  para vencer as partidas.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Como Começar */}
      <section className="py-16 px-4 bg-[#181D24]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Como Começar no <span className="text-[#FF4655]">Valorant</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Siga este guia passo a passo para começar sua jornada no Valorant da forma correta.
            </p>
          </motion.div>

          <div className="space-y-8">
            {howToSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-6"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-[#FF4655] rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {index + 1}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{step.name}</h3>
                  <p className="text-gray-300">{step.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Melhores Agentes para Iniciantes */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Melhores <span className="text-[#FF4655]">Agentes</span> para Iniciantes
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Comece com estes agentes mais simples e eficazes para aprender o básico do jogo.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="bg-[#181D24] border-[#222A35] hover:border-[#FF4655] transition-colors">
              <CardHeader>
                <Shield className="h-8 w-8 text-[#00D4AA] mb-2" />
                <CardTitle className="text-white">Sage</CardTitle>
                <Badge className="w-fit bg-[#00D4AA] text-black">Sentinela</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 mb-4">
                  Ideal para iniciantes. Pode curar aliados, ressuscitar e criar barreiras defensivas.
                </p>
                <div className="text-sm text-gray-400">
                  <strong>Por que escolher:</strong> Habilidades de suporte, perdoa erros, útil sempre.
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#181D24] border-[#222A35] hover:border-[#FF4655] transition-colors">
              <CardHeader>
                <Zap className="h-8 w-8 text-[#F94144] mb-2" />
                <CardTitle className="text-white">Phoenix</CardTitle>
                <Badge className="w-fit bg-[#F94144] text-white">Duelista</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 mb-4">
                  Agente agressivo com habilidades de auto-cura e controle de área com fogo.
                </p>
                <div className="text-sm text-gray-400">
                  <strong>Por que escolher:</strong> Auto-suficiente, bom para aprender entrada.
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#181D24] border-[#222A35] hover:border-[#FF4655] transition-colors">
              <CardHeader>
                <Map className="h-8 w-8 text-[#FF8500] mb-2" />
                <CardTitle className="text-white">Brimstone</CardTitle>
                <Badge className="w-fit bg-[#FF8500] text-white">Controlador</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 mb-4">
                  Controlador simples com smokes e habilidades de suporte para a equipe.
                </p>
                <div className="text-sm text-gray-400">
                  <strong>Por que escolher:</strong> Fácil de usar, ensina posicionamento.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Dicas para Melhorar */}
      <section className="py-16 px-4 bg-[#181D24]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Dicas para <span className="text-[#FF4655]">Melhorar</span> no Valorant
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Estratégias comprovadas para acelerar seu progresso e subir de rank.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-[#0f141c] border-[#222A35]">
              <CardHeader>
                <Crosshair className="h-8 w-8 text-[#FF4655] mb-2" />
                <CardTitle className="text-white">Melhore sua Mira</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-300">• Pratique 15-30min diários no campo de tiro</p>
                <p className="text-gray-300">• Use Aim Lab ou Kovaak's para treino específico</p>
                <p className="text-gray-300">• Mantenha crosshair na altura da cabeça</p>
                <p className="text-gray-300">• Ajuste sensibilidade para sua preferência</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0f141c] border-[#222A35]">
              <CardHeader>
                <Map className="h-8 w-8 text-[#FF4655] mb-2" />
                <CardTitle className="text-white">Aprenda os Mapas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-300">• Decore callouts de cada mapa</p>
                <p className="text-gray-300">• Pratique smokes e lineups</p>
                <p className="text-gray-300">• Entenda rotações e timings</p>
                <p className="text-gray-300">• Assista pro players jogando</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0f141c] border-[#222A35]">
              <CardHeader>
                <Headphones className="h-8 w-8 text-[#FF4655] mb-2" />
                <CardTitle className="text-white">Comunicação</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-300">• Use microfone para callouts</p>
                <p className="text-gray-300">• Seja claro e objetivo</p>
                <p className="text-gray-300">• Mantenha positividade</p>
                <p className="text-gray-300">• Escute as informações da equipe</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0f141c] border-[#222A35]">
              <CardHeader>
                <BookOpen className="h-8 w-8 text-[#FF4655] mb-2" />
                <CardTitle className="text-white">Estude o Jogo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-300">• Assista suas próprias partidas</p>
                <p className="text-gray-300">• Analise erros e acertos</p>
                <p className="text-gray-300">• Estude estratégias profissionais</p>
                <p className="text-gray-300">• Tenha um mentor experiente</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-[#181D24]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Perguntas <span className="text-[#FF4655]">Frequentes</span>
            </h2>
            <p className="text-xl text-gray-300">
              Respostas para as dúvidas mais comuns sobre Valorant.
            </p>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-[#0f141c] border-[#222A35]">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300">{faq.answer}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FF4655] to-[#E63946]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto para Dominar o Valorant?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Acelere seu progresso com mentores profissionais. Aprenda estratégias avançadas, 
              corrija erros e suba de rank mais rapidamente.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-[#FF4655] hover:bg-gray-100"
                onClick={() => navigate("/mentor-valorant")}
              >
                <Users className="h-5 w-5 mr-2" />
                Encontrar Mentor Agora
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-[#FF4655]"
                onClick={() => navigate("/mentorias?game=valorant")}
              >
                <Trophy className="h-5 w-5 mr-2" />
                Ver Todas as Mentorias
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ComoJogarValorant; 